import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { configureShopify } from "@/services/shopifyService";
import { useAuth } from "@/context/AuthContext";

export type EmailSettings = {
  klaviyoApiKey: string;
  omnisendApiKey: string;
};

const validateSettings = (settings: EmailSettings) => {
  return settings.klaviyoApiKey.trim() || settings.omnisendApiKey.trim();
};

type InputField = {
  id: string;
  label: string;
  placeholder: string;
  type?: string;
  propertyName: keyof EmailSettings;
};

const EMAIL_FIELDS: InputField[] = [
  {
    id: "klaviyo_api_key",
    label: "Klaviyo API Key",
    placeholder: "pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    type: "password",
    propertyName: "klaviyoApiKey",
  },
  {
    id: "omnisend_api_key",
    label: "Omnisend API Key",
    placeholder: "om_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    type: "password",
    propertyName: "omnisendApiKey",
  },
];

const EmailSettingsTab = () => {
  const { toast } = useToast();
  const { brand } = useAuth();
  const [settings, setSettings] = useState({
    klaviyoApiKey: brand?.klaviyo_api_key || "",
    omnisendApiKey: brand?.omnisend_api_key || "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = () => {
    if (!validateSettings(settings)) {
      toast({
        title: "Missing information",
        description: "Please provide your Klaviyo API key",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      configureShopify(settings);

      setIsSaving(false);
      toast({
        title: "Settings saved",
        description: "Email settings have been saved successfully.",
      });
    } catch (error) {
      setIsSaving(false);
      toast({
        title: "Error saving settings",
        description: "There was a problem saving your email settings.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const field = EMAIL_FIELDS.find((f) => f.id === id);

    if (field) {
      setSettings((prev) => ({
        ...prev,
        [field.propertyName]: value,
      }));
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Email Settings</CardTitle>
        <CardDescription>Configure your email service credentials to enable sending discount codes</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {EMAIL_FIELDS.map((field) => (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{field.label}</Label>
            <Input
              id={field.id}
              type={field.type || "text"}
              placeholder={field.placeholder}
              value={settings[field.propertyName]}
              onChange={handleInputChange}
            />
            <p className="text-sm text-muted-foreground">Enter your {field.label.toLowerCase()}</p>
          </div>
        ))}
      </CardContent>
      <CardFooter>
        <Button onClick={handleSaveSettings} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Settings"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EmailSettingsTab;
